import React, {useState} from 'react';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import AppTableHeaderCell from '../../../Presentation/components/ui/AppTableHeaderCell';
import {AppText} from '../../../Presentation/components/ui/Typography';
import {Marketer} from '../models/Marketer';
import {UpdateMarketerProfileRequest, UpdateMarketerPasswordRequest} from '../models/UpdateMarketer';
import {Trash2, Key, User} from 'lucide-react';
import Modal from '../../../Presentation/components/ui/Modal';
import Button from '../../../Presentation/components/ui/Button';
import {useNotification} from '../../../Services/useNotification';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {BaseTableProps} from '../../../types/table.types';
import EditMarketerForm from './EditMarketerForm';
import ChangePasswordForm from './ChangePasswordForm';
import {useTableExport, useExportConfig} from '../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../Presentation/components/ui/ExportButton';
import {ExportFormat} from '../../../types/export.types';
import {ExportFormatters} from '../../../Presentation/utils/exportFormatters';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

interface MarketersTableProps extends BaseTableProps<Marketer> {
  onDeleteMarketer?: (id: number) => Promise<boolean>;
  onUpdateProfile?: (id: number, data: UpdateMarketerProfileRequest) => Promise<any>;
  onUpdatePassword?: (id: number, data: UpdateMarketerPasswordRequest) => Promise<any>;
  deletingMarketerId?: number | null;
  exportable?: boolean;
  loading?: boolean;
}

const MarketersTable: React.FC<MarketersTableProps> = ({
  data: marketers,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onDeleteMarketer,
  onUpdateProfile,
  onUpdatePassword,
  deletingMarketerId,
  showPageSizeSelector = true,
  showPaginationInfo = true,
  exportable = false,
  loading = false
}) => {
  const {hasPermission} = useAuthorization();
  const {success: showSuccess} = useNotification();
  const [deleteMarketer, setDeleteMarketer] = useState<Marketer | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editMarketer, setEditMarketer] = useState<Marketer | null>(null);
  const [changePasswordMarketer, setChangePasswordMarketer] = useState<Marketer | null>(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Export configuration
  const {createConfig} = useExportConfig<Marketer>();

  const exportConfig = createConfig([
    {key: 'id', title: 'معرف المسوق'},
    {key: 'username', title: 'اسم المستخدم'},
    {key: 'fullName', title: 'الاسم الكامل'},
    {key: 'email', title: 'البريد الإلكتروني'},
    {key: 'phoneNumber', title: 'رقم الهاتف'},
    {key: 'status', title: 'الحالة', formatter: ExportFormatters.activeStatus},
    {key: 'role', title: 'الدور'},
    {
      key: 'lastLocationUpdate',
      title: 'آخر تحديث للموقع',
      formatter: (value) => {
        if (value && typeof value === 'string') {
          return new Date(value).toLocaleDateString('en-US');
        }
        return 'غير محدد';
      }
    },
    {
      key: 'createdAt',
      title: 'تاريخ الإنشاء',
      formatter: (value) => {
        if (value && typeof value === 'string') {
          return new Date(value).toLocaleDateString('en-US');
        }
        return 'غير محدد';
      }
    },
    {
      key: 'latitude',
      title: 'خط العرض',
      formatter: (value) => value ? value.toString() : 'غير محدد'
    },
    {
      key: 'longitude',
      title: 'خط الطول',
      formatter: (value) => value ? value.toString() : 'غير محدد'
    }
  ], 'مسوقين', 'تصدير بيانات المسوقين', 'تصدير جميع بيانات المسوقين مع التفاصيل الكاملة');

  // Export hook
  const {exportData, isExporting, canExport, recordCount} = useTableExport({
    data: marketers,
    config: exportConfig
  });

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    await exportData(format);
  };

  const handleDelete = async () => {
    if (!deleteMarketer) {return;}
    setDeleteLoading(true);
    try {
      if (onDeleteMarketer) {
        const success = await onDeleteMarketer(deleteMarketer.id as number);
        if (success) {
          showSuccess('تم حذف المسوق بنجاح');
        }
      }
      setDeleteMarketer(null);
    } catch (err) {
      StandardizedErrorHandler.getInstance().handleError(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUpdateProfile = async (data: UpdateMarketerProfileRequest) => {
    if (!editMarketer) {return;}
    setUpdateLoading(true);
    try {
      if (onUpdateProfile) {
        await onUpdateProfile(editMarketer.id as number, data);
        showSuccess('تم تحديث بيانات المسوق بنجاح');
        setEditMarketer(null);
      }
    } catch (err) {
      StandardizedErrorHandler.getInstance().handleError(err);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleUpdatePassword = async (data: UpdateMarketerPasswordRequest) => {
    if (!changePasswordMarketer) {return;}
    setPasswordLoading(true);
    try {
      if (onUpdatePassword) {
        await onUpdatePassword(changePasswordMarketer.id as number, data);
        showSuccess('تم تغيير كلمة مرور المسوق بنجاح');
        setChangePasswordMarketer(null);
      }
    } catch (err) {
      StandardizedErrorHandler.getInstance().handleError(err);
    } finally {
      setPasswordLoading(false);
    }
  };


  const columns: TableColumn<Marketer>[] = [
    {
      key: 'id',
      title: <AppTableHeaderCell label="رقم المسوق" />,
      render: (marketer: Marketer) => (
        <AppText>{marketer.id}</AppText>
      )
    },
    {
      key: 'full_name',
      title: <AppTableHeaderCell label="الاسم الكامل" />,
      render: (marketer: Marketer) => <AppText>{marketer.name}</AppText>
    },
    {
      key: 'phone_number',
      title: <AppTableHeaderCell label="رقم الجوال" />,
      render: (marketer: Marketer) => <AppText>{marketer.phone}</AppText>
    }

  ];

  return (
    <div className="flex-1 min-h-[calc(100vh-80px)] flex flex-col">
      {/* Export Button */}
      {exportable && (
        <div className="mb-4 flex justify-end">
          <ExportButton
            onExport={handleExport}
            isExporting={isExporting}
            canExport={canExport}
            recordCount={recordCount}
            showDropdown={true}
          />
        </div>
      )}

      <div className="overflow-x-auto">
        <AppTable
          columns={columns}
          data={marketers}
          rowKey={(row, index) => `marketer-${row.id}-${index || 0}`}
          emptyText="لا توجد نتائج"
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          showPageSizeSelector={showPageSizeSelector}
          showPaginationInfo={showPaginationInfo}
          loading={loading}
          loadingRows={pageSize || 10}
          loadingMessage="جاري تحميل المسوقين..."
        actions={(marketer) => {
          const actions = [];

          // Edit marketer - Requires marketers update permission
          if (hasPermission(PERMISSIONS.UPDATE_MARKETERS) || hasPermission(PERMISSIONS.MANAGE_MARKETERS)) {
            actions.push({
              label: 'تعديل بيانات المسوق',
              icon: <User className="text-brand-600" size={20} />,
              onClick: () => {
                setEditMarketer(marketer);
              }
            });
          }

          // Change password - Requires marketers update permission
          if (hasPermission(PERMISSIONS.UPDATE_MARKETERS) || hasPermission(PERMISSIONS.MANAGE_MARKETERS)) {
            actions.push({
              label: 'تغيير كلمة المرور',
              icon: <Key className="text-orange-600" size={20} />,
              onClick: () => {
                setChangePasswordMarketer(marketer);
              }
            });
          }

          // Delete marketer - Requires marketers delete permission
          if (hasPermission(PERMISSIONS.DELETE_MARKETERS) || hasPermission(PERMISSIONS.MANAGE_MARKETERS)) {
            actions.push({
              label: deletingMarketerId === marketer.id ? 'جاري الحذف...' : 'حذف المسوق',
              icon: <Trash2 className="text-red-600" size={20} />,
              onClick: () => {
                if (deletingMarketerId !== marketer.id) {
                  setDeleteMarketer(marketer);
                }
              },
              danger: true
            });
          }

          return actions;
        }}
        />
      </div>
      <Modal open={!!deleteMarketer} onClose={() => setDeleteMarketer(null)} title="تأكيد الحذف">
        <div className="text-center mb-6">
          هل أنت متأكد من أنك تريد حذف هذا المسوق؟<br />
          <span className="font-bold text-red-600">{deleteMarketer?.name}</span>
        </div>
        <div className="flex gap-4 justify-center">
          <Button variant="ghost" onClick={() => setDeleteMarketer(null)} disabled={deleteLoading}>
            إلغاء
          </Button>
          <Button variant="danger" onClick={handleDelete} isLoading={deleteLoading}>
            حذف
          </Button>
        </div>
      </Modal>

      {/* Edit Marketer Form */}
      {editMarketer && (
        <EditMarketerForm
          marketer={editMarketer}
          onSubmit={handleUpdateProfile}
          onCancel={() => setEditMarketer(null)}
          loading={updateLoading}
          open={!!editMarketer}
        />
      )}

      {/* Change Password Form */}
      {changePasswordMarketer && (
        <ChangePasswordForm
          marketer={changePasswordMarketer}
          onSubmit={handleUpdatePassword}
          onCancel={() => setChangePasswordMarketer(null)}
          loading={passwordLoading}
          open={!!changePasswordMarketer}
        />
      )}
    </div>
  );
};

export default MarketersTable;
